/* eslint-disable max-lines */
import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FrameAnimation } from 'common-package';
import { Progress } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import { AnimatePresence, motion } from 'framer-motion';
import { Icon } from '@fuxi/eevee-icon';
import { Dialog } from 'antd-mobile';
import Decimal from 'decimal.js';
import lookie from 'lookie';
import { useLocation } from 'react-router';
import { throttle } from 'lodash-es';
import { useDocumentVisibility, useGetState, useLatest, useMount, useSetState, useUnmount } from 'ahooks';
import { useNav, useSocketIO } from '@/hooks';
import textImg from '@/assets/image/text-bg.png';
import { selectCurrentServingOrder, setCurrentServingOrder } from '@/store/service';
import { formatSeconds, isNewSoftwareVersion, RandomPicker } from '@/utils';
import { RobotExceptionEventsHandler } from '@/components/robot-exception-prompt/utils';
import { ALL_PAGES, CURRENT_ORDER_ID, HAS_SHOWED_TIPS_MODAL, TOAST_CONTAINER_ID } from '@/constant';
import service from '@/service';
import RobotExceptionPrompt, { RobotExceptionPromptType } from '@/components/robot-exception-prompt';
import { Header, Toast, When } from '@/components';
import musicImg from '@/assets/image/real-time-music.png';
import awesomeTextImg from '@/assets/image/awesome-text.png';
import { processManualImg } from '../massage-control';
import RealTimeMode, { RealTimeModeRefAttrs } from './real-time-mode';
import { MusicThemeText, RandomArrayPicker, SOUND_WAVE_BG_WHITE, THEME_MODE, VALID_BUBBLE_COORD } from './utils';
import GuideModal from './guide-modal';
import ModeSwitch from './mode-switch';
import SwitchBgMusic from './switch-bg-music';
import immerToastify from './immersion-toastify';

import cx from './index.module.scss';

interface Props {}

const randomCoords = new RandomPicker(VALID_BUBBLE_COORD);
const BUBBLE_DURATION_SEC = 10;

type TextIDs = 'mainText' | 'subText1' | 'subText2' | 'subText3';

interface ImmersionProviderValue {
  robotState: {
    force: number;
    height: number;
    rfPower: number;
    hotStonePower: number;
  };
  massageState: RobotState;
}

export const ImmersionValueContext = createContext<ImmersionProviderValue>({
  robotState: {
    force: 0,
    height: 0,
    rfPower: 0,
    hotStonePower: 0,
  },
  massageState: null,
});

const ImmersionMode: React.FC<Props> = () => {
  const nav = useNav();
  const location = useLocation();
  const currentServingOrder = useSelector(selectCurrentServingOrder);
  const latestOrder = useLatest<GetServiceOrderListResponse>(currentServingOrder);
  const [currentIsSlideLeft, setCurrentIsSlideLeft] = useState(false);
  const [currentThemeId, setCurrentThemeId] = useState<SetBackgroundMusicNameParams['name']>(null);
  const [currentSelectThemeId, setCurrentSelectThemeId] = useState<SetBackgroundMusicNameParams['name']>(null);
  const [isUseThemeBoxAnimate, setIsUseThemeBoxAnimate] = useState(false);
  const [massageState, setMassageState, getMassageState] = useGetState<RobotState>(null);
  const [isPlayingBgm, setIsPlayingBgm] = useState(false);
  const [steps, setSteps] = useGetState<GetMassageTaskInfoResponse[]>([]);
  const [currentStep, setCurrentStep] = useGetState(0);
  const [taskSequence, setTaskSequence] = useState<GetTaskSequenceStatus>(null);
  const documentVisibility = useDocumentVisibility();
  const [isGuideModalShow, setIsGuideModalShow] = useState(false);
  const [commandText, setCommandText, getCommandText] = useGetState<GetVoiceCommandResponse>([]);
  const documentVisibilityRef = useLatest(documentVisibility);
  const [isRealTimeMode, setIsRealTimeMode] = useState(location?.state?.isRealTimeMode || false);
  const [intensifyTreatmentStatus, setIntensifyTreatmentStatus] = useState<
    GetMachineStateResponse['intensify_treatment_status']
  >({} as any);
  const [currentProcess, setCurrentProcess, getCurrentProcess] = useGetState<Service.SubStep>(null);
  const [progressInfo, setProgressInfo] = useState<GetTaskProgressResponse>({} as GetTaskProgressResponse);
  const [bgmInfo, setBgmInfo] = useSetState<GetMachineStateResponse['background_music']>({
    enable: false,
    playing: false,
    name: null,
  });
  const shouldUpdateTaskSequence = useRef(false);
  const [robotState, setRobotState] = useState({
    force: 0,
    height: 0,
    rfPower: 0,
    hotStonePower: 0,
  });
  const currentOrderIdRef = useLatest(currentServingOrder?.orderId);
  const dispatch = useDispatch();
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [storedHitTargetInfo, setStoredHitTargetInfo] = useState<GetShockwaveHitTargetInfo>(null);
  const robotExceptionPromptRef = useRef<RobotExceptionPromptType>(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const playerRef = useRef<HTMLVideoElement>(null);
  const [currentVideoPlayIndex, setCurrentVideoPlayIndex] = useState(-1);
  const [isVideoFadeOut, setIsVideoFadeOut] = useState(false);
  const palpationTimeoutTimer = useRef(null);
  const isIgnoreTaskFinished = useRef(false);
  const [massageHeadRealtimeStatus, setMassageHeadRealtimeStatus] = useState<MassageHeadRealtimeStatus>({
    actual_force: 0,
    model_position: [0, 0],
    human_touched: false,
  });
  const [bubbleTexts, setBubbleTexts] = useSetState({
    mainText: '',
    subText1: '',
    subText2: '',
    subText3: '',
  });
  const [bubbleCoordsInfo, setBubbleCoordsInfo] = useSetState<Record<TextIDs, typeof VALID_BUBBLE_COORD[0]>>({
    mainText: null,
    subText1: null,
    subText2: null,
    subText3: null,
  });
  const [awesomePointRecordLabelInfo, setAwesomePointRecordLabelInfo] = useSetState({
    show: false,
    offset: 0,
  });
  const showAwesomePointLabelTimer = useRef<NodeJS.Timeout>(null);
  const [awesomePointRecordList, setAwesomePointRecordList] = useState([]);
  const [isBgMusicSwitcherOpen, setIsBgMusicSwitcherOpen] = useState(false);
  const realTimeModeRef = useRef<RealTimeModeRefAttrs>(null);

  const clearToastLoading = useRef<{ [key: string]: () => void }>({});

  const { socketInstance, initSocket, destroySocket } = useSocketIO({ autoCloseOnUnmount: false });

  const currentActiveIndex = useMemo(
    () => THEME_MODE.findIndex(item => item.themeId === currentThemeId),
    [currentThemeId]
  );

  const isAnimateText = useMemo(
    () => massageState === 'Running_Massaging' && currentThemeId == currentSelectThemeId && bgmInfo?.playing,
    [bgmInfo?.playing, currentSelectThemeId, currentThemeId, massageState]
  );

  const alwaysTextsLength = useLatest(
    useMemo(() => commandText?.filter(item => item.priority === 0)?.length, [commandText])
  );
  const normalTextsLength = useLatest(
    useMemo(() => commandText?.filter(item => item.priority !== 0)?.length, [commandText])
  );

  /** 获取语音常驻和非常驻文本 */
  const textPicker = useMemo(() => {
    if (!commandText?.length) {
      return {
        hasInit: false,
        getAlwaysShowText: () => '',
        getNormalText: () => '',
      };
    }

    /** 常驻文本 */
    const alwaysTexts = new RandomPicker(
      commandText
        .filter(item => item.priority === 0)
        ?.map(item => item.texts || [])
        ?.flat()
    );
    /** 非常驻文本 */
    const normalTexts = new RandomArrayPicker(
      commandText.filter(item => item.priority !== 0)?.map(item => item.texts || [])
    );

    return {
      hasInit: true,
      getAlwaysShowText: alwaysTexts.getRandomItem.bind(alwaysTexts),
      getNormalText: normalTexts.getItem.bind(normalTexts),
    };
  }, [commandText]);

  const latestTextPicker = useLatest(textPicker);

  // 是否展示新的进度条交互
  const isShowNewProcess = useMemo(
    () =>
      taskSequence?.task_sequence?.length === 2 &&
      taskSequence?.task_sequence?.[0]?.state === 'Success' &&
      isNewSoftwareVersion(currentServingOrder?.device?.softwareVersion, '1.7.0'),
    [taskSequence, currentServingOrder]
  );

  // 当前方案是否是冲击波
  const currentStepIsShockwave = useMemo(() => steps[currentStep]?.massageItem === 'Shockwave', [currentStep, steps]);

  // 当前是否在打击线
  const currentIsHitLine = useMemo(
    () => storedHitTargetInfo?.currentHitTarget?.targetType === 'Route',
    [storedHitTargetInfo]
  );

  // 获取机械臂碰撞检测状态
  const handleGetCollisionCheckState = useCallback(async () => {
    const data = await socketInstance.current.getCollisionCheckState();

    if (['Activated', 'Activated_Passed', 'Activated_Denied'].includes(data?.state)) {
      nav(ALL_PAGES.MASSAGE_CONTROL, {
        state: {
          tab: 'func',
        },
      });
    }
  }, [nav, socketInstance]);

  /** 处理机器异常事件 */
  const hanldeRobotExceptionEvents = useCallback(
    (code: MassageRuntime.RobotExceptionEvents) => {
      if (!code) return;

      const errorOccurLevel = RobotExceptionEventsHandler[code]?.level;
      if (errorOccurLevel === 'l3') {
        //  视觉的人体异常也属于l3级别的异常，但不需要弹窗，因此未进行配置
        const l3ParamName = RobotExceptionEventsHandler[code]?.l3Params;

        if (l3ParamName) {
          // 清除之前同类型的 L3 提示
          if (clearToastLoading.current[code]) {
            clearToastLoading.current[code]?.();
          }

          clearToastLoading.current[code] = immerToastify?.l3[RobotExceptionEventsHandler[code]?.l3Params]()?.clear;
        }
      } else if (errorOccurLevel === 'l4') {
        if (code === 'POSSIBLE_SLIPPING_WARNING') {
          if (currentServingOrder?.productItems?.[0] === 'Shockwave') {
            immerToastify.l4.swSlippingWarning();
          } else if (currentServingOrder?.productItems?.[0] === 'Massage') {
            immerToastify.l4.massageSlippingWarning();
          }
        } else {
          const l4ParamsName = RobotExceptionEventsHandler[code]?.l4Params;
          l4ParamsName && immerToastify.l4[l4ParamsName]();
        }
      }
    },
    [currentServingOrder]
  );

  /** 处理机器异常恢复事件 */
  const hanldeRobotExceptionRecoverEvents = useCallback((code: MassageRuntime.RobotExceptionEvents) => {
    const errorRecoverLevel = RobotExceptionEventsHandler[code]?.level;

    if (errorRecoverLevel === 'l3') {
      clearToastLoading.current?.[code]?.();
    }
  }, []);

  const onRobotEvent: onRoboticEventListener = useCallback(
    event => {
      // console.log('[onRobotEvent]:', event);
      switch (event.name) {
        // 状态改变事件
        case 'state_change':
          const state = event.details?.state;

          setMassageState(state);
          if (state === 'Running_Massaging' && shouldUpdateTaskSequence.current) {
            socketInstance.current.getTaskSequenceStatus().then(data => {
              shouldUpdateTaskSequence.current = false;
              setTaskSequence(data);
            });
          }

          if (['Preparing_WaitingInstallation', 'Preparing_MovingToMatrixInstallation'].includes(state)) {
            setCurrentProcess('Installation');
          } else if (
            [
              'Preparing_HumanDetecting',
              'Preparing_AcupointsAnalyzing',
              'Preparing_KnotsMarking',
              'Preparing_HumanDetectingTimeout',
              'Preparing_AcupointsCalibration',
            ].includes(state)
          ) {
            setCurrentProcess('VisualCheck');
          } else if (
            [
              'Preparing_WaitingDrawCustomRoute',
              'Preparing_DrawCustomRoute',
              'Preparing_WaitingTechRouteGenerating',
              'Preparing_Setting',
            ].includes(state)
          ) {
            setCurrentProcess('CustomPalpation');
          } else if (['Running_Massaging', 'Running'].includes(state)) {
            setCurrentProcess('Massage');
          }
          break;
        case 'step_change':
          if (['VisualCheckAndPalpation', 'VisualCheck'].includes(event.details.step)) {
            setCurrentProcess('VisualCheckAndPalpation');
          }
          break;
        /** 进度变化 */
        case 'progress_change':
          setProgressInfo(event.details);

          // 射频方案，每过15分钟提示一遍涂抹射频膏
          if (
            latestOrder.current?.productItems?.[0] === 'RadioFrequency' &&
            (event.details?.total - event.details?.remaining) % 900 === 0 &&
            // 未开始时首次不提示
            event.details?.remaining !== event.details?.total
          ) {
            immerToastify.l4.showRFTherapyTip();
          }
          break;
        case 'background_music_change':
          setBgmInfo(event?.details);
          break;
        case 'intensify_treatment_status_change':
          setIntensifyTreatmentStatus(event.details);
          break;
        case 'error_fire':
          const errorOccurCode = event.details.error;

          const errorOccurLevel = RobotExceptionEventsHandler[errorOccurCode]?.level;

          // 沉浸模式l3,l4的错误进行提示，l0l1l2的错误跳回按摩页
          if (['l3', 'l4'].includes(errorOccurLevel)) {
            hanldeRobotExceptionEvents(errorOccurCode);
          } else if (['l0', 'l1', 'l2'].includes(errorOccurLevel)) {
            if (errorOccurCode === 'ARM_ERROR') {
              robotExceptionPromptRef.current?.hanldeRobotExceptionEvents('ARM_ERROR');
            } else {
              nav(ALL_PAGES.MASSAGE_CONTROL);
            }
          }

          break;
        case 'error_done':
          const errorRecoverCode = event.details.error;

          const errorRecoverLevel = RobotExceptionEventsHandler[errorRecoverCode]?.level;

          if (errorRecoverLevel === 'l3') {
            hanldeRobotExceptionRecoverEvents(errorRecoverCode);
            // 机械臂断联恢复时按摩任务会暂停，回到按摩控制页面
            if (errorRecoverCode === 'ARM_RECONNECTING') {
              nav(ALL_PAGES.MASSAGE_CONTROL);
            }
          }
          break;
        case 'task_finished':
          socketInstance.current.getTaskSequenceStatus().then(taskSequence => {
            // 当前是人工治疗，跳回按摩控制页面
            if (taskSequence?.task_sequence?.findIndex(item => item.state !== 'Success') === 1) {
              nav(ALL_PAGES.MASSAGE_CONTROL, {
                state: {
                  tab: 'func',
                },
              });
            }
          });
          break;
        // 自动结束事件
        case 'task_sequence_result':
          if (isIgnoreTaskFinished.current) return;

          const isSuccess = event.details.success;
          const finishedReason = event.details?.results?.finishedReason;

          let params: QuitMassageFromEventParams = {
            serviceOrderId: currentOrderIdRef.current,
            success: isSuccess,
          };
          // 是否是用户手动结束订单
          let isEndFromUser = false;

          if (isNewSoftwareVersion(latestOrder.current?.device?.softwareVersion, '1.7.0')) {
            isEndFromUser = finishedReason === 'User';
            if (latestOrder.current?.productItems?.includes('Shockwave')) {
              params.shockwaveProbeCount = event.details?.results?.shockwaveProbeCount;
            }
          }

          service.massage.quitMassageFromEvent(params).then(async response => {
            if (window.location.pathname !== '/service/immersion-mode') {
              return;
            }

            if (isSuccess) {
              if (latestOrder.current?.productItems?.includes('Shockwave')) {
                // 正常结束并且有剩余次数 弹保养提示的弹窗
                if (
                  (!!response.data?.probeMaxUseCount || !!response.data?.orderMaxUseCount) &&
                  getCurrentProcess() === 'Massage'
                ) {
                  Dialog.alert({
                    title: '磁震波设备保养提示',
                    content:
                      latestOrder.current?.shockwaveType === 1 ? (
                        <div>
                          <span>磁震波每击打{response.data?.probeMaxUseCount / 10000}万次需要保养，</span>
                          <br />
                          <span>
                            当前设备已击打
                            <span style={{ color: '#05f' }}>{event.details?.results?.shockwaveProbeCount}</span>次,
                          </span>
                          <br />
                          <span>若不处理会影响顾客的理疗体验，</span>
                          <br />
                          <span>建议您及时保养。有任何问题可以联系客服。</span>
                        </div>
                      ) : (
                        <div>
                          <span>当前设备已达到较高打击次数</span>
                          <br />
                          <span>为了不影响顾客的理疗体验，建议您及时保养， </span>
                          <br />
                          <span>有任何问题可以联系客服。</span>
                        </div>
                      ),
                    confirmText: '我知道了',
                    onConfirm: async () => {
                      //强制用户进行动作复测
                      nav(ALL_PAGES.ACTION_RETEST, {
                        state: {
                          isAllProcessSuccess: !isEndFromUser,
                          orderId: currentOrderIdRef.current,
                          isNeedShowDrawerAfterFinish: true,
                        },
                      });
                    },
                    className: cx('custom-dialog'),
                  });
                } else {
                  nav(ALL_PAGES.ACTION_RETEST, {
                    state: {
                      isAllProcessSuccess: !isEndFromUser,
                      orderId: currentOrderIdRef.current,
                      isNeedShowDrawerAfterFinish: true,
                    },
                  });
                }
              } else if (latestOrder.current?.productItems?.includes('RadioFrequency')) {
                nav(ALL_PAGES.BODY_REMEASUREMENT, {
                  state: {
                    isAllProcessSuccess: !isEndFromUser,
                    orderId: currentOrderIdRef.current,
                    isNeedShowDrawerAfterFinish: true,
                  },
                });
              } else {
                nav(isEndFromUser ? ALL_PAGES.MEMBER_DETAIL : ALL_PAGES.HOME, {
                  state: {
                    orderId: currentOrderIdRef.current,
                    isNeedShowDrawerAfterFinish: true,
                    customerId: latestOrder?.current?.customerId,
                  },
                });
              }
            } else {
              if (latestOrder.current?.productItems?.includes('Shockwave')) {
                robotExceptionPromptRef.current?.showOrderErrorPrompt({
                  navUrl: ALL_PAGES.ACTION_RETEST,
                  navOptions: {
                    state: {
                      isAllProcessSuccess: false,
                      orderId: currentOrderIdRef.current,
                      isNeedShowDrawerAfterFinish: false,
                    },
                  },
                  orderId: currentOrderIdRef.current,
                  shockwaveProbeCount: event.details?.results?.shockwaveProbeCount,
                  finishedReason,
                });
              } else if (latestOrder.current?.productItems?.includes('RadioFrequency')) {
                robotExceptionPromptRef.current?.showOrderErrorPrompt({
                  navUrl: ALL_PAGES.BODY_REMEASUREMENT,
                  navOptions: {
                    state: {
                      isAllProcessSuccess: false,
                      orderId: currentOrderIdRef.current,
                      isNeedShowDrawerAfterFinish: false,
                    },
                  },
                  orderId: currentOrderIdRef.current,
                  finishedReason,
                });
              } else {
                robotExceptionPromptRef.current?.showOrderErrorPrompt({
                  navUrl: ALL_PAGES.MEMBER_DETAIL,
                  orderId: currentOrderIdRef.current,
                  finishedReason,
                });
              }
            }
          });
          break;
        /** 矩阵式艾灸 or 颈部按摩头 检测 */
        case 'moxibustion_check_state_change':
          if (['Activated', 'Activated_Passed', 'Activated_Denied'].includes(event.details.state)) {
            nav(ALL_PAGES.MASSAGE_CONTROL);
          }
          break;
        case 'massage_head_realtime_status':
          setMassageHeadRealtimeStatus(event.details);
          break;
        case 'force_change':
          setRobotState(p => ({
            ...p,
            force: event.details?.force,
          }));
          break;
        case 'height_change':
          setRobotState(p => ({
            ...p,
            height: event.details?.height,
          }));
          break;
        case 'hotstone_change':
          setRobotState(p => ({
            ...p,
            hotStonePower: event.details.power ? +new Decimal(event.details.power).mul(100).toFixed(0) : 0,
          }));
          break;
        case 'radio_frequency_states_change':
          setRobotState(p => ({
            ...p,
            rfPower: isNaN(Math.round((event.details?.power * 10000) / 100))
              ? 0
              : Math.round((event.details?.power * 10000) / 100),
          }));
          break;
        case 'immerse_mode_cool_points':
          const recordList = event.details?.cool_points;

          realTimeModeRef.current?.playVideo();
          clearTimeout(showAwesomePointLabelTimer.current);

          setAwesomePointRecordList(recordList);
          setAwesomePointRecordLabelInfo({
            show: true,
            offset: recordList?.[recordList?.length - 1],
          });
          showAwesomePointLabelTimer.current = setTimeout(() => setAwesomePointRecordLabelInfo({ show: false }), 2000);
          break;
      }
    },
    [
      currentOrderIdRef,
      getCurrentProcess,
      hanldeRobotExceptionEvents,
      hanldeRobotExceptionRecoverEvents,
      latestOrder,
      nav,
      setAwesomePointRecordLabelInfo,
      setBgmInfo,
      setCurrentProcess,
      setMassageState,
      socketInstance,
    ]
  );

  const handleTouchStart = useCallback(event => {
    touchStartX.current = event.touches[0].clientX;
  }, []);

  const handleTouchMove = useCallback(event => {
    touchEndX.current = event.changedTouches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!(touchEndX.current && touchStartX.current)) return;

    const difference = touchEndX.current - touchStartX.current;

    if (Math.abs(difference) < 30) {
      return;
    }

    setCurrentIsSlideLeft(difference < 0);

    const newIndex = currentActiveIndex + (difference < 0 ? 1 : -1);

    if (newIndex >= 0 && newIndex <= THEME_MODE?.length - 1) {
      setCurrentThemeId(THEME_MODE[newIndex]?.themeId as any);
    } else if (newIndex < 0) {
      // 当前为第一个主题时向右滑，设置为最后一个
      setCurrentThemeId(THEME_MODE[THEME_MODE?.length - 1].themeId as any);
    } else if (newIndex === THEME_MODE.length) {
      // 当前为最后一个主题时向左滑，设置为最后一个
      setCurrentThemeId(THEME_MODE[0].themeId as any);
    }
    touchStartX.current = 0;
    touchEndX.current = 0;
  }, [currentActiveIndex]);

  // 初始化
  const init = useCallback(async () => {
    const { data: orderInfo } = await service.service.getServingOrderByOperator();
    if (!orderInfo) {
      const previousOrderId = lookie.get(CURRENT_ORDER_ID);
      if (previousOrderId) {
        const { data } = await service.service.getServiceOrder(previousOrderId);

        nav(data?.state === 'Completed' ? ALL_PAGES.HOME : ALL_PAGES.MEMBER_DETAIL, {
          state: {
            orderId: previousOrderId,
            isNeedShowDrawerAfterFinish: true,
            customerId: latestOrder?.current?.customerId,
          },
        });
      } else {
        nav(ALL_PAGES.MEMBER_DETAIL);
      }
      return;
    }

    const { data: commandData } = await service.device.getVoiceCommand(orderInfo?.orderId);
    setCommandText(commandData || []);

    // 判断是否显示过提示框
    const hasShowedTipsModal = lookie.get(HAS_SHOWED_TIPS_MODAL);

    if (!hasShowedTipsModal?.[orderInfo?.orderId]) {
      setIsGuideModalShow(true);
      lookie.set(HAS_SHOWED_TIPS_MODAL, { [orderInfo?.orderId]: true });
    }

    dispatch(setCurrentServingOrder(orderInfo));
    const { data: connectInfo } = await service.device.getConnectionInfo(orderInfo?.deviceId);
    initSocket(connectInfo);

    socketInstance.current.onConnect(() => {
      console.log('onconnect');

      immerToastify.clear();

      clearTimeout(palpationTimeoutTimer.current);
      setIsSocketConnected(true);
    });

    socketInstance.current.onDisconnect(reason => {
      setIsSocketConnected(false);

      if (reason !== 'io client disconnect') {
        // 当前是否在手诊中
        const currentStepIsVisualCheck = ['VisualCheck', 'VisualCheckAndPalpation'].includes(getCurrentProcess());

        if (currentStepIsVisualCheck) {
          immerToastify.l3.networkException();
        } else {
          immerToastify.l4.offline();
        }

        if (currentStepIsVisualCheck && documentVisibilityRef.current === 'visible') {
          palpationTimeoutTimer.current = setTimeout(() => {
            robotExceptionPromptRef.current?.hanldeRobotExceptionEvents('NETWORK_BROKEN');
          }, 30 * 1000);
        }
      }
    });

    // 注册socketio s2c事件
    socketInstance.current.onRobotEvent(onRobotEvent);

    const { data: taskInfo } = await service.massage.getMassageTaskInfo(orderInfo?.orderId);
    setSteps(taskInfo);
  }, [
    dispatch,
    documentVisibilityRef,
    getCurrentProcess,
    initSocket,
    latestOrder,
    nav,
    onRobotEvent,
    setCommandText,
    setSteps,
    socketInstance,
  ]);

  // 获取按摩任务相关信息
  const getMassageTask = useCallback(async () => {
    if (!socketInstance.current) return;

    // 判断当前是否有订单
    const { data: currentOrder } = await service.service.getServingOrderByOperator();

    dispatch(setCurrentServingOrder(currentOrder || ({} as any)));

    if (!currentOrder) {
      const previousOrderId = lookie.get(CURRENT_ORDER_ID);
      if (previousOrderId) {
        const { data } = await service.service.getServiceOrder(previousOrderId);

        nav(data?.state === 'Completed' ? ALL_PAGES.HOME : ALL_PAGES.MEMBER_DETAIL, {
          state: {
            orderId: previousOrderId,
            isNeedShowDrawerAfterFinish: true,
            customerId: latestOrder?.current?.customerId,
          },
        });
      } else {
        nav(ALL_PAGES.MEMBER_DETAIL, {
          state: {
            orderId: previousOrderId,
            isNeedShowDrawerAfterFinish: true,
          },
        });
      }
      return;
    }

    // 机器人状态
    const machineInfo = await socketInstance.current.getMachineState();
    setRobotState({
      force: machineInfo?.force,
      height: machineInfo?.height,
      rfPower: isNaN(machineInfo?.radio_frequency?.power)
        ? 0
        : Math.round((machineInfo?.radio_frequency?.power * 10000) / 100) || 0,
      hotStonePower: machineInfo?.hotstone?.power ? +new Decimal(machineInfo?.hotstone?.power).mul(100).toFixed(0) : 0,
    });

    if (isNewSoftwareVersion(currentOrder?.device?.softwareVersion, '1.10.0')) {
      setAwesomePointRecordList(machineInfo?.immerse_mode_cool_points);
    }

    setIntensifyTreatmentStatus(machineInfo?.intensify_treatment_status || ({} as any));

    if (!machineInfo?.background_music?.name) {
      Toast.show('该机器版本不支持沉浸音乐');
      nav(ALL_PAGES.MASSAGE_CONTROL);
      return;
    }

    setBgmInfo(machineInfo?.background_music);
    setMassageState(machineInfo.state);

    // 任务进度
    const data = await socketInstance.current?.getTaskProgress();

    setProgressInfo(data);
    // 任务详情
    const taskSequence = await socketInstance.current.getTaskSequenceStatus();
    setTaskSequence(taskSequence);
    const curStep = taskSequence?.task_sequence?.findIndex(item => item.state !== 'Success');
    setCurrentStep(curStep);

    if (curStep > 0) {
      shouldUpdateTaskSequence.current = true;
    }

    // 处理异常
    if (machineInfo?.error_state?.length) {
      const errCode = machineInfo?.error_state?.[0];

      const errorOccurLevel = RobotExceptionEventsHandler[errCode]?.level;

      // 沉浸模式l3,l4的错误进行提示，l0l1l2的错误跳回按摩页
      if (['l3', 'l4'].includes(errorOccurLevel)) {
        hanldeRobotExceptionEvents(errCode);
      } else if (['l0', 'l1', 'l2'].includes(errorOccurLevel)) {
        if (errCode === 'ARM_ERROR') {
          robotExceptionPromptRef.current?.hanldeRobotExceptionEvents('ARM_ERROR');
        } else {
          nav(ALL_PAGES.MASSAGE_CONTROL);
        }
        return;
      }
    }

    // 获取冲击波目标状态
    const hitTargetInfo = await socketInstance.current.getHitTargetStates();
    setStoredHitTargetInfo(hitTargetInfo);

    // 获取当前步骤
    const _currentProcess = await socketInstance.current.getCurrentStep();
    if (
      _currentProcess?.step === 'Massage' &&
      [
        'Preparing_HumanDetecting',
        'Preparing_AcupointsAnalyzing',
        'Preparing_KnotsMarking',
        'Preparing_HumanDetectingTimeout',
      ].includes(machineInfo.state)
    ) {
      setCurrentProcess('VisualCheck');
    } else if (
      _currentProcess?.step === 'Massage' &&
      ['Preparing_WaitingInstallation', 'Preparing_InstallingWiring', 'Preparing_MovingToMatrixInstallation'].includes(
        machineInfo.state
      )
    ) {
      setCurrentProcess('Installation');
    } else {
      setCurrentProcess(_currentProcess?.step);
    }
    handleGetCollisionCheckState();
  }, [
    dispatch,
    handleGetCollisionCheckState,
    hanldeRobotExceptionEvents,
    latestOrder,
    nav,
    setBgmInfo,
    setCurrentProcess,
    setCurrentStep,
    setMassageState,
    socketInstance,
  ]);

  // 获取气泡坐标，保证新获取的气泡位置与现在正在展示的不重合
  const getNewBubbleCoords = useCallback((currentBubbleCoordsInfo: typeof bubbleCoordsInfo) => {
    const currentCoordsList = Object.values(currentBubbleCoordsInfo || []);
    const random = randomCoords.getRandomItem();

    if (currentCoordsList?.some(item => item?.left === random?.left && item?.top === random?.top)) {
      return getNewBubbleCoords(currentBubbleCoordsInfo);
    } else {
      return random;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 获取常驻文本，保证新获取的文本现在正在展示的不重合
  const getNewMainText = useCallback(
    (currentBubbleTexts: typeof bubbleTexts) => {
      if (!getCommandText()?.length) {
        return;
      }

      const currentTexts = Object.values(currentBubbleTexts || {});

      const newMainText = latestTextPicker.current.getAlwaysShowText();

      if (alwaysTextsLength.current < 6) {
        return newMainText;
      }

      if (currentTexts?.filter(Boolean)?.some(item => item === newMainText)) {
        return getNewMainText(currentBubbleTexts);
      } else {
        return newMainText;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // 获取非常驻文本，保证新获取的文本现在正在展示的不重合
  const getNewNormalText = useCallback((currentBubbleTexts: typeof bubbleTexts) => {
    if (!getCommandText()?.length) {
      return;
    }

    const currentTexts = Object.values(currentBubbleTexts || {});
    const newNormalText = latestTextPicker.current.getNormalText();

    if (normalTextsLength?.current < 6) {
      return newNormalText;
    }

    if (currentTexts?.filter(Boolean)?.some(item => item === newNormalText)) {
      return getNewNormalText(currentBubbleTexts);
    } else {
      return newNormalText;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 单轮动画完成后设置气泡文本和位置
  const throttleSetBubbleText = useMemo(
    () => (textId: TextIDs) =>
      throttle(state => {
        if (state.transform === 'scale(1.6)') {
          setBubbleCoordsInfo(prev => ({ [textId]: getNewBubbleCoords(prev) } as any));
          setBubbleTexts(
            prev =>
              ({
                [textId]: textId === 'mainText' ? getNewMainText(prev) : getNewNormalText(prev),
              } as any)
          );
        }
      }, 400),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onVideoUpdate = useCallback(event => {
    // 检查当前视频是否要播放完毕
    setIsVideoFadeOut(event?.target?.currentTime + 3 > event?.target.duration);
  }, []);

  useMount(() => {
    init();
    setBubbleCoordsInfo({
      mainText: randomCoords.getRandomItem(),
      subText1: randomCoords.getRandomItem(),
      subText2: randomCoords.getRandomItem(),
      subText3: randomCoords.getRandomItem(),
    });
  });

  useUnmount(() => {
    destroySocket();
    playerRef.current?.removeEventListener('timeupdate', onVideoUpdate);
    if (isNewSoftwareVersion(latestOrder.current.device?.softwareVersion, '1.10.0') && latestOrder.current.deviceId) {
      service.lanInterface.endRealTimeDisplay(latestOrder.current.deviceId);
    }
  });

  useEffect(() => {
    if (currentServingOrder?.orderId && isSocketConnected) {
      getMassageTask();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentServingOrder?.orderId, isSocketConnected]);

  useEffect(() => {
    if (bgmInfo?.playing) {
      setIsPlayingBgm(true);
      bgmInfo?.enable && setCurrentVideoPlayIndex(THEME_MODE.findIndex(item => item.themeId === bgmInfo?.name));
    } else {
      setIsPlayingBgm(false);
      setCurrentVideoPlayIndex(-1);
    }
  }, [bgmInfo]);

  // 分多个effect防止其它值改变影响
  useEffect(() => {
    setCurrentThemeId(bgmInfo?.name);
    setCurrentSelectThemeId(bgmInfo?.name);
  }, [bgmInfo?.name]);

  useEffect(() => {
    textPicker?.hasInit &&
      setBubbleTexts({
        mainText: textPicker.getAlwaysShowText(),
        subText1: textPicker.getNormalText(),
        subText2: textPicker.getNormalText(),
        subText3: textPicker.getNormalText(),
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textPicker]);

  useEffect(() => {
    if (isRealTimeMode) return;

    if (currentActiveIndex === currentVideoPlayIndex && massageState === 'Running_Massaging') {
      playerRef.current?.play();
      playerRef?.current?.removeEventListener('timeupdate', onVideoUpdate);
      playerRef.current?.addEventListener('timeupdate', onVideoUpdate);
    } else {
      playerRef.current?.pause();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentActiveIndex, currentVideoPlayIndex, massageState, isRealTimeMode]);

  useEffect(() => {
    if (!isNewSoftwareVersion(latestOrder.current.device?.softwareVersion, '1.10.0')) return;
    if (!isSocketConnected) return;

    if (isRealTimeMode) {
      socketInstance.current?.startRealTimeDisplay();
    } else {
      socketInstance.current?.endRealTimeDisplay();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRealTimeMode, isSocketConnected]);

  // 沉浸模式中展示正在进行人体检测
  useEffect(() => {
    if (['VisualCheck', 'VisualCheckAndPalpation', 'CustomPalpation'].includes(currentProcess)) {
      clearToastLoading.current['humanDetecting']?.();
      clearToastLoading.current['humanDetecting'] = immerToastify.l3.humanDetecting()?.clear;
    } else {
      clearToastLoading.current['humanDetecting']?.();
    }
  }, [currentProcess]);

  return (
    <div className={cx('immersion-wrapper', { 'is-real-time-mode': isRealTimeMode })}>
      {!!currentSelectThemeId ? (
        <div className={cx('ImmersionMode')}>
          <Header
            textColor="white"
            showBorder={false}
            title=""
            backConfig={{
              text: '服务详情',
              action: () => {
                setTimeout(
                  () =>
                    nav(ALL_PAGES.MASSAGE_CONTROL, {
                      state: {
                        tab: 'func',
                        showSkeleton: true,
                      },
                    }),
                  200
                );
              },
            }}
          />
          <RobotExceptionPrompt
            currentProcess={currentProcess}
            socketInstance={socketInstance}
            ref={robotExceptionPromptRef}
            isIgnoreTaskFinished={isIgnoreTaskFinished}
          />
          <div
            className={cx('bg-box')}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{ backgroundImage: `url(${THEME_MODE.find(item => item.themeId === currentThemeId)?.imageUrl})` }}
          >
            {isAnimateText && (
              <div className={cx('voice-tips')}>
                <img src={textImg} alt="" />
              </div>
            )}
            {THEME_MODE?.map((item, index) => (
              <div
                className={cx('img-item', {
                  active: currentThemeId === item.themeId,
                  'is-slide-left': currentIsSlideLeft,
                })}
                key={index}
              >
                {currentActiveIndex === index && (
                  <video
                    ref={playerRef}
                    className={cx('custom-video-player', {
                      show: currentVideoPlayIndex === index,
                      fadeOut: isVideoFadeOut,
                    })}
                    muted
                    loop
                    poster={THEME_MODE[index]?.imageUrl}
                    preload="auto"
                    src={THEME_MODE[index]?.videoUrl}
                  />
                )}
                <img src={item.imageUrl} alt="" className={cx('video-img')} />
              </div>
            ))}
            <When condition={currentThemeId !== currentSelectThemeId}>
              <div className={cx('indicator')}>
                {[...new Array(5)].map((_, index) => (
                  <div className={`${cx['item']} ${index === currentActiveIndex && cx['active']}`} key={index} />
                ))}
              </div>
            </When>
            <When condition={currentSelectThemeId !== currentThemeId || bgmInfo?.enable == false}>
              <div className={cx('theme-desc-box', { animate: isUseThemeBoxAnimate })}>
                <div className={cx('theme-name')}>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentThemeId}
                      initial={{ x: currentIsSlideLeft ? 10 : -10, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      exit={{ x: currentIsSlideLeft ? -10 : 10, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      {MusicThemeText[currentThemeId]}
                    </motion.div>
                  </AnimatePresence>
                </div>
                <div
                  className={cx('use-theme')}
                  onClick={() => {
                    setIsUseThemeBoxAnimate(true);
                    setTimeout(async () => {
                      /** [HACK] 处理动画 */
                      setCurrentSelectThemeId(currentThemeId);
                      await socketInstance.current.setBackgroundMusicName({ name: currentThemeId as any });
                      await socketInstance.current.setBackgroundMusic({ enable: true });
                      setIsUseThemeBoxAnimate(false);
                    }, 300);
                  }}
                >
                  使用
                </div>
              </div>
            </When>
          </div>
          <div className={cx('guide-box')} onClick={() => setIsGuideModalShow(true)}>
            <Icon name="star" color="#fff" size={20} />
          </div>
          <div className={cx('bubble-box')}>
            {/* 常驻文本和普通文本 */}
            {['mainText', 'subText1', 'subText2', 'subText3'].map(item => (
              <motion.div
                key={item}
                className={cx('bubble-item', { hide: !isAnimateText }, bubbleCoordsInfo?.[item]?.pos)}
                animate={
                  isAnimateText
                    ? {
                        transform: ['scale(0.6)', 'scale(1)', 'scale(1.6)'],
                        opacity: [0, 0.2, 0.4, 1, 1, 1, 1, 0],
                      }
                    : false
                }
                onUpdate={throttleSetBubbleText(item as TextIDs)}
                transition={{
                  delay: item === 'subText1' ? 3.5 : item === 'subText2' ? 7 : item === 'subText3' ? 10.5 : 0,
                  duration: BUBBLE_DURATION_SEC,
                  repeat: Infinity,
                  ease: 'linear',
                  repeatDelay: 0.6,
                }}
                style={{ ...bubbleCoordsInfo?.[item] }}
              >
                小易师傅，{bubbleTexts?.[item]}
              </motion.div>
            ))}
          </div>
          <ImmersionValueContext.Provider value={{ robotState, massageState }}>
            <RealTimeMode
              show={isRealTimeMode}
              ref={realTimeModeRef}
              massageHeadRealtimeStatus={massageHeadRealtimeStatus}
            />
          </ImmersionValueContext.Provider>
          <div className={cx('footer', { 'is-real-mode': isRealTimeMode })}>
            <div className={cx('massage-task-info')}>
              <div className={cx('filter-bg-box')} />
              <div className={cx('info-wrapper')}>
                <When
                  condition={
                    isNewSoftwareVersion(currentServingOrder?.device?.softwareVersion, '1.10.0') && currentStep === 0
                  }
                >
                  <div className={cx('mode-change')} onClick={e => e?.stopPropagation()}>
                    <ModeSwitch
                      value={isRealTimeMode ? 'real-time' : 'heal'}
                      steps={[
                        {
                          value: 'heal',
                          label: '疗愈',
                        },
                        {
                          value: 'real-time',
                          label: '实时',
                        },
                      ]}
                      onChange={value => setIsRealTimeMode(value === 'real-time')}
                    />
                  </div>
                </When>
                <div className={cx('user-info')}>{currentServingOrder?.customerName}的服务单</div>
                <div className={cx('info-box')}>
                  <div className={cx('task-info')}>
                    <div className={cx('plan-name')}>{currentServingOrder?.productName}</div>
                    <div className={cx('remain-time')}>
                      <div className={cx('remaining')}>
                        {isShowNewProcess && taskSequence?.task_sequence?.[1]?.state !== 'Created' ? (
                          currentStepIsShockwave ? (
                            <div className={cx('retaining-manual-box')}>
                              <span className={cx('remain-time-text')}>剩余{progressInfo?.remaining}次/</span>
                              <span className={cx('beaten-shockwave')}>
                                已击打{progressInfo?.order_actual_shockwave_hits}次
                              </span>
                            </div>
                          ) : (
                            <div className={cx('retaining-manual-box')}>
                              <span className={cx('retaining-shockwave')}>
                                已理疗{formatSeconds(progressInfo?.order_massage_duration)}
                              </span>
                            </div>
                          )
                        ) : (
                          <div className={cx('remaining-machine-box')}>
                            {currentStepIsShockwave && steps?.[currentStep]?.massageType !== 'ManualMassage' && (
                              <span>
                                {intensifyTreatmentStatus?.is_intensifying ? '' : currentIsHitLine ? '走线' : '痛点'}
                              </span>
                            )}
                            <span className={cx('remain-time-text')}>剩余</span>
                            <span className={cx('num', { 'no-gap': currentStepIsShockwave })}>
                              {intensifyTreatmentStatus?.is_intensifying
                                ? formatSeconds(intensifyTreatmentStatus.remaining_duration)
                                : currentStepIsShockwave
                                ? /**
                                   *  1.8.0 以下版本的机器 remaining_line 和 remaining_nodule 字段有误
                                   *  在 1.8.0 及以上版本修正
                                   */
                                  isNewSoftwareVersion(currentServingOrder?.device?.softwareVersion, '1.8.0') &&
                                  // 人工单取remaining字段
                                  currentServingOrder?.modelConfig?.[0]?.value !== 'normal-manual'
                                  ? currentIsHitLine
                                    ? progressInfo?.remaining_line
                                    : progressInfo?.remaining_nodule
                                  : progressInfo?.remaining
                                : formatSeconds(progressInfo?.remaining)}
                            </span>
                            {currentStepIsShockwave && <span className={cx('unit')}>次</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={cx('progress-bar-area')}>
                    <When
                      condition={
                        isNewSoftwareVersion(currentServingOrder?.device?.softwareVersion, '1.10.0') &&
                        currentStep === 0
                      }
                    >
                      <div className={cx('awesome-point-box', { 'has-manual-treat': steps?.length > 1 })}>
                        <div
                          style={{ left: `${awesomePointRecordLabelInfo?.offset}%` }}
                          className={cx('record-label', { show: awesomePointRecordLabelInfo?.show })}
                        >
                          爽点已记录
                        </div>
                        {awesomePointRecordList?.map(item => (
                          <div className={cx('awesome-point-item')} key={item} style={{ left: `${item}%` }} />
                        ))}
                      </div>
                    </When>
                    <Progress
                      className={cx('progress-wrapper')}
                      percent={
                        intensifyTreatmentStatus?.is_intensifying
                          ? (intensifyTreatmentStatus?.remaining_duration / intensifyTreatmentStatus?.total_duration) *
                            100
                          : currentStep > 0
                          ? 100
                          : progressInfo?.progress
                      }
                      showInfo={false}
                      size={['100%', 12]}
                      strokeColor={intensifyTreatmentStatus?.is_intensifying ? '#0055FF' : '#fff'}
                      trailColor={intensifyTreatmentStatus?.is_intensifying ? '#D4DFF2' : '#FFFFFF4D'}
                    />
                    {steps?.length > 1 && (
                      <div
                        className={cx('manual-treat', {
                          'process-bg': isShowNewProcess && taskSequence?.task_sequence?.[1]?.state === 'Started',
                        })}
                      >
                        <Progress
                          className={cx('progress-wrapper')}
                          percent={
                            isShowNewProcess && taskSequence?.task_sequence?.[1]?.state === 'Started'
                              ? 100
                              : currentStep === 1
                              ? progressInfo?.progress
                              : 0
                          }
                          showInfo={false}
                          size={['100%', 12]}
                          strokeColor="#fff"
                          trailColor="#FFFFFF4D"
                        />
                        <div className={cx('line')} />
                        {/* 机器版本大于等于1.7.0的版本显示新交互 */}
                        {isShowNewProcess && (
                          <>
                            {taskSequence?.task_sequence?.[1]?.state === 'Created' && getMassageState() === 'Idle' && (
                              <>
                                {currentStepIsShockwave ? (
                                  <>
                                    <div className={cx('common')}>
                                      已击打{progressInfo?.order_actual_shockwave_hits}次
                                    </div>
                                    <div className={cx('triangle')} />
                                  </>
                                ) : (
                                  <>
                                    <div className={cx('common')}>
                                      已理疗{formatSeconds(progressInfo?.order_massage_duration)}
                                    </div>
                                    <div className={cx('triangle')} />
                                  </>
                                )}
                              </>
                            )}
                            {taskSequence?.task_sequence?.[1]?.state === 'Started' && (
                              <img alt="process" src={processManualImg} className={cx('process-img')} />
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {isRealTimeMode ? (
              <div className={cx('real-time-mode-footer')}>
                <div
                  className={cx('awesome-point')}
                  onClick={() =>
                    realTimeModeRef.current?.playVideo(() => {
                      socketInstance.current.immerseModeCoolPoint();
                    })
                  }
                >
                  <img src={awesomeTextImg} alt="" />
                </div>
                <div className={cx('real-time-music-wrapper')} onClick={() => setIsBgMusicSwitcherOpen(true)}>
                  <img src={musicImg} alt="" />
                </div>
              </div>
            ) : (
              <div className={cx('music-wrapper')}>
                <div className={cx('music-info')}>
                  <div className={cx('music-title')}>疗愈音乐</div>
                  <div className={cx('music-name')}>{MusicThemeText[currentThemeId]}</div>
                </div>
                {isPlayingBgm && (
                  <FrameAnimation
                    className={cx('frame-animation')}
                    count={260}
                    aspectRatio={5.18}
                    width={230}
                    duration={5}
                    picDirection="column"
                    url={SOUND_WAVE_BG_WHITE}
                    onSinglePlayComplete={() => {
                      // 单帧播放完成后，如果机器人未在播音乐，则隐藏动画
                      if (!bgmInfo?.playing) {
                        setIsPlayingBgm(false);
                      }
                    }}
                  />
                )}
              </div>
            )}
          </div>
          <GuideModal open={isGuideModalShow} setOpen={setIsGuideModalShow} isRealTimeMode={isRealTimeMode} />
          <ToastContainer
            className={cx({ 'is-real-mode': isRealTimeMode })}
            enableMultiContainer={true}
            containerId={TOAST_CONTAINER_ID.ImmersionMode}
            closeButton={false}
            hideProgressBar={true}
            draggable={false}
            position="bottom-left"
            closeOnClick={false}
            pauseOnFocusLoss={false}
            pauseOnHover={false}
          />
          <SwitchBgMusic
            socketInstance={socketInstance}
            bgmInfo={bgmInfo}
            currentThemeId={currentThemeId}
            open={isBgMusicSwitcherOpen}
            setOpen={setIsBgMusicSwitcherOpen}
          />
        </div>
      ) : (
        <div className={cx('skeleton')} />
      )}
    </div>
  );
};

export default ImmersionMode;
